/**
 * La mesa de trabajo del encargo — compartida por las dos orillas.
 *
 * Soham la ve desde la cotización (VistaEncargoTaller); el aliado la ve desde su
 * propia superficie (Aliado.jsx). Ambos leen las mismas revisiones append-only:
 * el levantamiento original sellado primero, y encima cada avance con autor y
 * fecha. Corregir es agregar otra revisión, nunca editar una existente.
 */
import { useCallback, useEffect, useState } from 'react'
import * as store from '../datos/store.js'
import { usuario } from '../firebase.js'
import { piezasDeRamo, proyectaEncargo, NOMBRE_ESTADO, NOMBRE_TIPO } from '../motor/encargos.js'

const fechaCorta = iso => (iso || '').slice(0, 16).replace('T', ' · ')

export function BarraSimple({ titulo, migaja, atras, acciones }) {
  return (
    <div className="bar">
      {atras && (
        <button className="ib ico" onClick={atras} aria-label="Regresar">
          <span aria-hidden="true" style={{ fontSize: 22, lineHeight: 1 }}>‹</span>
        </button>
      )}
      <h1>{titulo}{migaja && <span className="crumb">{migaja}</span>}</h1>
      {acciones}
    </div>
  )
}

export function SelloEstado({ estado }) {
  return <span className="bdg" style={{ background: 'var(--negro)' }}>{NOMBRE_ESTADO[estado] || estado}</span>
}

/** Una foto del levantamiento: acepta dataURL directo o id de la colección fotos. */
function FotoChica({ f }) {
  const directa = typeof f === 'string' && f.startsWith('data:')
  const [src, setSrc] = useState(directa ? f : null)
  useEffect(() => {
    if (directa || !f) return undefined
    let vivo = true
    store.obtenerFoto(f).then(d => { if (vivo && d) setSrc(d) })
    return () => { vivo = false }
  }, [f, directa])
  return src ? <img className="ph xs" src={src} alt="" /> : <div className="ph xs" />
}

export function Piezas({ piezas }) {
  if (!piezas?.length) return null
  return piezas.map((p, i) => (
    <div className="card" key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ display: 'flex', gap: 3, flex: 'none' }}>
        {(p.fotos || []).length
          ? p.fotos.slice(0, 3).map((f, j) => <FotoChica key={j} f={f} />)
          : <div className="ph xs" />}
      </div>
      <div className="grow">
        <div className="t">{p.nombre}</div>
        <div className="s">{p.q} {p.unidad}{p.habitacion ? ` · ${p.habitacion}` : ''}</div>
      </div>
    </div>
  ))
}

export async function agregarRevision(encargoId, datos) {
  await store.guardar(`encargos/${encargoId}/revisiones`, {
    ...datos,
    autor: usuario()?.uid || 'local',
    autorCorreo: usuario()?.email || 'Soham (modo local)',
    fecha: new Date().toISOString()
  })
}

/** Fotos de una revisión: miniaturas que se expanden a ancho completo con un toque.
    Van embebidas como dataURL en la revisión — selladas con ella, inmutables como ella.
    La bitácora de la casa reusa esta misma pieza para las fotos de una intervención. */
export function FotosRevision({ fotos }) {
  const [grande, setGrande] = useState(null)
  if (!fotos?.length) return null
  return (
    <div style={{ marginTop: 8 }}>
      <div className="fotos" style={{ marginBottom: 0 }}>
        {fotos.map((f, i) => (
          <img key={i} src={f} alt={`Foto ${i + 1} de la revisión`}
            onClick={() => setGrande(grande === i ? null : i)} />
        ))}
      </div>
      {grande !== null && fotos[grande] && (
        <img src={fotos[grande]} alt="" onClick={() => setGrande(null)}
          style={{ width: '100%', borderRadius: 'var(--r)', marginTop: 8, cursor: 'zoom-out' }} />
      )}
    </div>
  )
}

export function Revisiones({ encargoId, ramo }) {
  const [revs, setRevs] = useState(null)
  /* El aliado solo puede leer revisiones de SU ramo (`firestore.rules`), y las
     reglas no filtran: rechazan la consulta entera. Sin el `where`, la pantalla del
     taller se quedaba en «Cargando revisiones…» para siempre — detectado en la
     refutación del 2026-08-22. El vendedor, que lee todo, sigue sin filtro. */
  useEffect(() => {
    const ordenar = lista => setRevs([...lista].sort((a, b) => (a.fecha || '').localeCompare(b.fecha || '')))
    return ramo
      ? store.suscribirConsulta(`encargos/${encargoId}/revisiones`, 'ramo', ramo, ordenar)
      : store.suscribir(`encargos/${encargoId}/revisiones`, ordenar)
  }, [encargoId, ramo])
  if (revs === null) return <div className="empty">Cargando revisiones…</div>
  if (!revs.length) return <div className="empty"><b>Sin revisiones todavía.</b><p>La primera será el levantamiento sellado.</p></div>
  return revs.map((r, i) => (
    <div className="card" key={r.id || i}>
      <div className="grow">
        <div className="t">
          {NOMBRE_TIPO[r.tipo] || r.tipo}
          {i === 0 && r.tipo === 'levantamiento' && ' — original, sellado'}
        </div>
        <div className="s">{r.autorCorreo} · {fechaCorta(r.fecha)}</div>
        {r.texto && <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{r.texto}</div>}
        {r.piezas?.length > 0 && (
          <div className="s" style={{ marginTop: 6 }}>
            {r.piezas.map(p => `${p.q} ${p.unidad} — ${p.nombre}`).join(' · ')}
          </div>
        )}
        <FotosRevision fotos={r.fotos} />
      </div>
    </div>
  ))
}

/* Tope de fotos por documento y de peso total. Firestore corta en 1 MiB; con
   6 fotos comprimidas (~40-80 KB cada una) queda holgura de sobra. */
const MAX_FOTOS_REVISION = 6
const MAX_PESO_FOTOS = 700000

/**
 * Captura de fotos con quitar en dos toques (marcar, anular) — el mismo gesto
 * que el levantamiento, sin diálogo del sistema. La usan las revisiones del
 * encargo y las intervenciones de la bitácora: quien registra en campo aprende
 * el gesto una sola vez.
 */
export function CapturaFotos({ fotos, onCambiar }) {
  const [dudosa, setDudosa] = useState(null)
  const [error, setError] = useState(null)

  const agregarFoto = async e => {
    const archivo = e.target.files?.[0]
    e.target.value = ''
    if (!archivo) return
    setError(null)
    try {
      const chica = await store.comprimirImagen(archivo)
      const peso = fotos.reduce((s, f) => s + f.length, 0) + chica.length
      if (peso > MAX_PESO_FOTOS) {
        setError('Las fotos ya pesan demasiado para un solo registro. Guarda este y agrega las que faltan en otro.')
        return
      }
      onCambiar([...fotos, chica])
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <>
      <div className="fotos" style={{ marginTop: 10, marginBottom: 10 }}>
        {fotos.map((f, i) => (
          <span className={`foto${dudosa === i ? ' dudosa' : ''}`} key={i}>
            <img src={f} alt={`Foto ${i + 1}`} onClick={() => setDudosa(dudosa === i ? null : i)} />
            {dudosa === i && (
              <button className="anular" onClick={() => {
                setDudosa(null)
                onCambiar(fotos.filter((_, j) => j !== i))
              }}>Anular</button>
            )}
          </span>
        ))}
        {fotos.length < MAX_FOTOS_REVISION && (
          <label className="add">
            +
            <input type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }} onChange={agregarFoto} />
          </label>
        )}
      </div>
      {error && <div className="s" style={{ color: 'var(--rojo, #b00020)', marginBottom: 8 }}>{error}</div>}
    </>
  )
}

export function FormRevision({ encargoId, ramo, tipo, rotulo = 'Agregar avance' }) {
  const [texto, setTexto] = useState('')
  const [fotos, setFotos] = useState([])
  const [ocupado, setOcupado] = useState(false)
  const hayContenido = texto.trim() || fotos.length > 0

  const enviar = async () => {
    if (!hayContenido) return
    setOcupado(true)
    try {
      const datos = { ramo, tipo, texto: texto.trim() }
      if (fotos.length) datos.fotos = fotos
      await agregarRevision(encargoId, datos)
      setTexto(''); setFotos([])
    } finally { setOcupado(false) }
  }

  return (
    <div className="card">
      <label className="f">
        <span>{rotulo}</span>
        <textarea value={texto} style={{ minHeight: 90 }}
          placeholder="Medidas ajustadas, material propuesto, dudas del desarrollo…"
          onChange={e => setTexto(e.target.value)} />
      </label>
      <CapturaFotos fotos={fotos} onCambiar={setFotos} />
      <button className="ib" disabled={ocupado || !hayContenido} onClick={enviar}>
        {ocupado ? 'Guardando…' : 'Guardar revisión'}
      </button>
    </div>
  )
}

/** La vista del encargo del lado de Soham, colgada de la cotización. */
export function VistaEncargoTaller({ cot, resolver, atras }) {
  const id = `${cot.id}--taller`
  const [encargo, setEncargo] = useState(undefined)
  const cargar = useCallback(() => { store.obtener('encargos', id).then(setEncargo) }, [id])
  useEffect(() => { cargar() }, [cargar])

  const piezas = piezasDeRamo(cot, 'taller', resolver)
  const enviar = async () => {
    const enc = proyectaEncargo(cot, 'taller', resolver, encargo || undefined)
    if (!enc) return
    await store.guardar('encargos', enc)
    await agregarRevision(id, {
      ramo: 'taller',
      tipo: 'levantamiento',
      texto: encargo
        ? 'Levantamiento actualizado por Soham.'
        : 'Levantamiento original — sellado.',
      piezas: enc.piezas
    })
    cargar()
  }

  return (
    <div className="app">
      <BarraSimple titulo="Encargo al taller" migaja={cot.nombre} atras={atras}
        acciones={encargo ? <SelloEstado estado={encargo.estado} /> : null} />
      <main>
        <div className="sec">Piezas del ramo en esta cotización</div>
        <Piezas piezas={piezas} />

        <div className="card">
          <div className="grow">
            <div className="t">{encargo ? 'El taller ya tiene este encargo' : 'El taller aún no lo recibe'}</div>
            <div className="s">
              {encargo
                ? `Última actualización: ${fechaCorta(encargo.actualizadoEn)}. Reenviar sella una nueva revisión del levantamiento; las anteriores no se tocan.`
                : 'Al enviarlo, el levantamiento queda sellado como la primera revisión y el aliado lo ve en su Central.'}
            </div>
          </div>
          <button className="ib pri" onClick={enviar} disabled={!piezas.length}>
            {encargo ? 'Actualizar levantamiento' : 'Enviar al taller'}
          </button>
        </div>

        {encargo && (
          <>
            <div className="sec">Revisiones</div>
            <Revisiones encargoId={id} />
            <FormRevision encargoId={id} ramo="taller" tipo="nota" rotulo="Nota para el taller" />
          </>
        )}
      </main>
    </div>
  )
}
